import { IDropdownOption, MessageBarType } from '@fluentui/react';
import { IconName, sort, useToast } from '@h2oai/ui-kit';
import { useCallback, useState } from 'react';

import {
  App,
  App_Visibility,
  AppInstance,
  AppInstance_Status,
  AppInstance_Visibility,
  AppInstanceLifecycle,
  AppPreconditionStatus,
} from '../../ai.h2o.cloud.appstore';
import { AdminAppService, AppService } from '../../services/api';
import { useApp, useEnv, useInstance } from '../../utils/hooks';
import { getToastErrorMessage, handleErrMsg } from '../../utils/utils';
import { useWorkspaces } from '../Orchestrator/WorkspaceProvider';
import { windowOpenErrorMessage } from './AppDetailPage';

interface Poller {
  run: () => Promise<void>;
  suspend: () => void;
}

const toastGroupId = 'AppDetailPage';

const hasInstances = (app: App) => {
  return app.instanceLifecycle !== AppInstanceLifecycle.APP_INSTANCE_LIFECYCLE_LINK;
};

export const useAppDetailsData = (appId: string) => {
  const env = useEnv();
  const { ACTIVE_WORKSPACE_NAME } = useWorkspaces();
  const { getInstancesList, getInstance } = useInstance();
  const { getAdminApps, getApps } = useApp();
  const { addToast } = useToast();
  const isAdmin = !!env?.menu?.user?.isAdmin;
  const userGetApps = isAdmin ? getAdminApps : getApps;
  const UserAppServices = isAdmin ? AdminAppService : AppService;
  const [app, setApp] = useState<App>({} as App);
  const [instances, setInstances] = useState<AppInstance[]>();
  const [myInstances, setMyInstances] = useState<AppInstance[]>();
  const [mainInstance, setMainInstance] = useState<AppInstance>();
  const [versions, setVersions] = useState<IDropdownOption[]>([]);
  const [activePollers, setActivePollers] = useState<string[]>([]);
  const [pollersList, setPollersList] = useState<Poller[]>([]);
  const [isInstancesLoading, setIsInstancesLoading] = useState(true);
  const [fatalError, setFatalError] = useState<any>();

  const loadApp = useCallback(async (): Promise<App | undefined> => {
    try {
      const { app: application } = await UserAppServices.getApp({ id: appId });
      setApp(application);
      const apps = await userGetApps({
        limit: 1000,
        offset: 0,
        visibility: App_Visibility.VISIBILITY_UNSPECIFIED,
        allUsers: true,
        name: application.name,
        latestVersions: false,
        withPreference: false,
        tags: [],
        conditionsStatus: AppPreconditionStatus.STATUS_UNSPECIFIED,
        visibilities: [],
      });
      setVersions(
        apps
          .map(({ id, version, visibility }) => ({
            key: id,
            text: visibility === App_Visibility.PRIVATE ? `${version}  (private)` : version,
            data: { icon: IconName.Accounts },
          }))
          .sort(sort<IDropdownOption>(['text'], 'semver', true))
      );
      return application;
    } catch (error: any) {
      setFatalError(error || {});
    }
    return undefined;
  }, [appId, userGetApps]);
  const loadInstances = useCallback(
    async (app: App, parent?: string) => {
      // Optimization to avoid the slow ListAppInstances RPC when there will be none
      if (!hasInstances(app)) {
        setIsInstancesLoading(false);
        return;
      }
      if (instances?.length === 0) setIsInstancesLoading(true);
      try {
        // For apps that are not runnable, consider any instance you can see as the main instance.
        // This is key for apps with MANAGED or EXTERNAL instance lifecycle.
        const [allInstances, myInstances] = await Promise.all([
          getInstancesList({
            appId,
            includeAppDetails: true,
            allUsers: true,
            visibility: AppInstance_Visibility.VISIBILITY_UNSPECIFIED,
            allWorkspaces: false,
            parent,
          }),
          getInstancesList({
            appId,
            includeAppDetails: true,
            allUsers: false,
            visibility: AppInstance_Visibility.VISIBILITY_UNSPECIFIED,
            allWorkspaces: false,
            parent,
          }),
        ]);

        setInstances(allInstances);
        void processPendingInstances(allInstances);
        // For apps that are not runnable, consider any instance you can see as the main instance.
        // This is key for apps with MANAGED or EXTERNAL instance lifecycle.
        if (app.runnable && myInstances.length) {
          setMainInstance(
            myInstances.find((i) =>
              [AppInstance_Status.DEPLOYED, AppInstance_Status.SUSPENDED, AppInstance_Status.PENDING].includes(i.status)
            )
          );
        } else {
          setMainInstance(allInstances.find((i) => i.status === AppInstance_Status.DEPLOYED));
        }
        setMyInstances(myInstances);
      } catch ({ message }) {
        addToast(
          getToastErrorMessage(
            `An error occurred while loading app instances: ${handleErrMsg(message as string)}`,
            toastGroupId
          )
        );
      } finally {
        setIsInstancesLoading(false);
      }
    },
    [appId, addToast, getInstancesList, ACTIVE_WORKSPACE_NAME]
  );

  const runApp = useCallback(
    async (
      visibility: AppInstance_Visibility = AppInstance_Visibility.ALL_USERS,
      profile = '',
      parent = ACTIVE_WORKSPACE_NAME,
      runAsAdmin = false
    ) => {
      const RunServices = runAsAdmin ? AdminAppService : AppService;
      addToast({ message: `Running app ${app.title}`, messageBarType: MessageBarType.success });
      try {
        const { instance } = await RunServices.runApp({ id: app.id, visibility: visibility, profile, parent });
        if (instance) {
          try {
            await loadInstances(app);
          } catch ({ message }) {
            addToast(
              getToastErrorMessage(
                `An error occurred while trying to update the instance list: : ${handleErrMsg(message as string)}`,
                toastGroupId
              )
            );
          }
          try {
            window.open(instance.location, '_blank');
          } catch ({ message }) {
            addToast(getToastErrorMessage(windowOpenErrorMessage, toastGroupId));
          }
        }
      } catch ({ message }) {
        addToast(getToastErrorMessage(`Could not run app: ${handleErrMsg(message as string)}`, toastGroupId));
      }
    },
    [app, loadInstances, addToast]
  );
  const runPoller = useCallback(
    (instance: AppInstance) => {
      const { id } = instance;
      const isPending = (status?: AppInstance_Status) => status === AppInstance_Status.PENDING;
      const poller = (() => {
        let timer: NodeJS.Timeout | undefined;
        let isSuspended = false;

        const run = async () => {
          try {
            const { status } = await getInstance(id, ACTIVE_WORKSPACE_NAME);

            if (isPending(status) && !isSuspended) {
              timer = setTimeout(run, 3000);
              return;
            }

            setActivePollers((prev) => prev.filter((i) => i !== id));
            if (app) void loadInstances(app);
            isSuspended = true;
            if (timer) clearTimeout(timer);
          } catch (error: any) {
            const is404 = error?.code === 'not_found';

            if (is404 && !isSuspended) {
              timer = setTimeout(run, 3000);
              return;
            }

            if (timer) clearTimeout(timer);
          }
        };
        const suspend = () => {
          isSuspended = true;
          if (timer) clearTimeout(timer);
        };

        void run();

        return { run, suspend };
      })();

      setPollersList((prev) => [...prev, poller]);
    },
    [getInstance, loadInstances, app, ACTIVE_WORKSPACE_NAME]
  );

  const processPendingInstances = useCallback(
    async (allInstances?: AppInstance[]) => {
      if (!allInstances?.length) return;

      const pendingInstances = allInstances.filter(
        ({ status, id }) => status === AppInstance_Status.PENDING && !activePollers.includes(id)
      );

      if (!pendingInstances.length) return;

      const pollers = [];
      for (const instance of pendingInstances) {
        void runPoller(instance);
        pollers.push(instance.id);
      }
      setActivePollers([...activePollers, ...pollers]);
    },
    [activePollers, ACTIVE_WORKSPACE_NAME]
  );

  return {
    app,
    instances,
    myInstances,
    mainInstance,
    versions,
    fatalError,
    isInstancesLoading,
    pollersList,
    loadApp,
    loadInstances,
    runApp,
    setApp,
  };
};
